import * as React from 'react';
import * as PropTypes from 'prop-types';
import Typography from '@material-ui/core/Typography';
import Day from '@material-ui/pickers/views/Calendar/Day';
import DayWrapper from '@material-ui/pickers/views/Calendar/DayWrapper';
import { Theme } from '@material-ui/core/styles';
import { VariantContext } from '@material-ui/pickers/wrappers/Wrapper';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { withStyles, WithStyles } from '@material-ui/core/styles';
import { findClosestEnabledDate } from '@material-ui/pickers/_helpers/date-utils';
import DateFnsUtils from '@date-io/date-fns';

export interface OutterCalendarProps {
  /** Custom renderer for day @DateIOType */
  renderDay?: (
    day: MaterialUiPickersDate,
    selectedDate: MaterialUiPickersDate,
    dayInCurrentMonth: boolean,
    dayComponent: JSX.Element
  ) => JSX.Element;
  /** Disable specific date @DateIOType */
  shouldDisableDate?: (day: MaterialUiPickersDate) => boolean;
}

export interface CalendarProps extends OutterCalendarProps, WithStyles<typeof styles, true> {
  /** Calendar Date @DateIOType */
  date: MaterialUiPickersDate;
  /** Calendar onChange */
  onChange: (date: MaterialUiPickersDate, isFinish?: boolean) => void;
  /** Min date @DateIOType */
  minDate?: MaterialUiPickersDate;
  /** Max date @DateIOType */
  maxDate?: MaterialUiPickersDate;
}

export interface CalendarState {
  currentMonth: MaterialUiPickersDate;
  lastDate?: MaterialUiPickersDate;
  loadingQueue: number;
}

export class InternalCalendar extends React.Component<CalendarProps, CalendarState> {
  static utils = new DateFnsUtils();

  static contextType = VariantContext;
  static propTypes: any = {
    renderDay: PropTypes.func,
    shouldDisableDate: PropTypes.func
  };

  static defaultProps: Partial<CalendarProps> = {
    minDate: new Date('1900-01-01'),
    maxDate: new Date('2100-01-01')
  };

  static getDerivedStateFromProps(nextProps: CalendarProps, state: CalendarState) {
    const { date: nextDate } = nextProps;

    if (!InternalCalendar.utils.isEqual(nextDate, state.lastDate)) {
      return {
        lastDate: nextDate,
        currentMonth: InternalCalendar.utils.startOfMonth(nextDate)
      };
    }

    return null;
  }

  state: CalendarState = {
    currentMonth: InternalCalendar.utils.startOfMonth(this.props.date),
    loadingQueue: 0,
  };

  componentDidMount() {
    const { date, minDate, maxDate } = this.props;

    if (this.shouldDisableDate(date)) {
      const closestEnabledDate = findClosestEnabledDate({
        date,
        utils: InternalCalendar.utils,
        minDate: InternalCalendar.utils.date(minDate),
        maxDate: InternalCalendar.utils.date(maxDate),
        disablePast: false,
        disableFuture: false,
        shouldDisableDate: this.shouldDisableDate,
      });

      this.handleDaySelect(closestEnabledDate, false);
    }
  }

  validateMinMaxDate = (day: MaterialUiPickersDate) => {
    const { minDate, maxDate } = this.props;

    return Boolean(
      (minDate && InternalCalendar.utils.isBeforeDay(day, InternalCalendar.utils.date(minDate))) ||
      (maxDate && InternalCalendar.utils.isAfterDay(day, InternalCalendar.utils.date(maxDate)))
    );
  };

  shouldDisableDate = (day: MaterialUiPickersDate) => {
    const { shouldDisableDate } = this.props;

    return this.validateMinMaxDate(day) || Boolean(shouldDisableDate && shouldDisableDate(day));
  };

  handleDaySelect = (day: MaterialUiPickersDate, isFinish = true) => {
    const { date } = this.props;

    this.props.onChange(InternalCalendar.utils.mergeDateAndTime(day, date), isFinish);
  };

  moveToDay = (day: MaterialUiPickersDate) => {
    if (day && !this.shouldDisableDate(day)) {
      if (InternalCalendar.utils.getMonth(day) !== InternalCalendar.utils.getMonth(this.state.currentMonth)) {
        this.setState({ currentMonth: InternalCalendar.utils.startOfMonth(day) });
      }

      this.handleDaySelect(day, false);
    }
  };

  private renderWeekNames = () => {
    return (
      <>
        {InternalCalendar.utils.getWeekdays().map((day, index) => (
          <Typography
            key={index} // eslint-disable-line react/no-array-index-key
            variant="caption"
            style={{
              width: 36,
              margin: '0 2px',
              textAlign: 'center',
              color: `#283845`
            }}
          >
            {day}
          </Typography>
        ))}
      </>
    );
  }

  private renderWeeks = () => {
    const { classes } = this.props;
    const weeks = InternalCalendar.utils.getWeekArray(this.state.currentMonth);

    return weeks.map(week => (
      <div key={`week-${week[0]!.toString()}`} className={classes.week}>
        {this.renderDays(week)}
      </div>
    ));
  };

  private renderDays = (week: MaterialUiPickersDate[]) => {
    const { date, renderDay } = this.props;

    const now = InternalCalendar.utils.date();
    const selectedDate = InternalCalendar.utils.startOfDay(date);
    const currentMonthNumber = InternalCalendar.utils.getMonth(this.state.currentMonth);

    return week.map(day => {
      const disabled = this.shouldDisableDate(day);
      const isDayInCurrentMonth = InternalCalendar.utils.getMonth(day) === currentMonthNumber;

      let dayComponent = (
        <Day
          disabled={disabled}
          current={InternalCalendar.utils.isSameDay(day, now)}
          hidden={!isDayInCurrentMonth}
          selected={InternalCalendar.utils.isSameDay(selectedDate, day)}
        >
          {InternalCalendar.utils.getDayText(day)}
        </Day>
      );

      if (renderDay) {
        dayComponent = renderDay(day, selectedDate, isDayInCurrentMonth, dayComponent);
      }

      return (
        <DayWrapper
          value={day}
          key={day!.toString()}
          disabled={disabled}
          dayInCurrentMonth={isDayInCurrentMonth}
          onSelect={this.handleDaySelect}
        >
          {dayComponent}
        </DayWrapper>
      );
    });
  };

  render() {
    return (
      <React.Fragment>
        <div 
          style={{ 
            display: `flex`, 
            justifyContent: `center`,  
            alignItems: `center`,
            maxHeight: 16
          }}
        >
          {this.renderWeekNames()}
        </div>
        <div>{this.renderWeeks()}</div>
      </React.Fragment>
      
    );
  }
}

export const styles = (theme: Theme) => ({
  transitionContainer: {
    minHeight: 36 * 6,
    marginTop: theme.spacing(1.5),
  },
  progressContainer: {
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  week: {
    display: 'flex',
    justifyContent: 'center',
  },
});

export default withStyles(styles, {
  name: 'MuiPickersCalendar',
  withTheme: true,
})(InternalCalendar);
